import React, { useState } from 'react';
import {
  Alert,
  Card,
  Row,
  Col,
  Progress,
  Table,
  Tag,
  Space,
  Typography,
  DatePicker,
  Select,
  Button,
  Empty,
} from 'antd';
import {
  DollarOutlined,
  ShoppingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useBillingOverview, useRevenueReport } from '../../hooks';
import { MetricCard, BarChart } from '../../components';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export const BillingDashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs(),
  ]);
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  const { overview, loading, error: overviewError, refetch } = useBillingOverview();
  const { report, loading: reportLoading, error: reportError, fetchReport } = useRevenueReport();

  const handleRefresh = () => {
    fetchReport({
      startDate: dateRange[0].format('YYYY-MM-DD'),
      endDate: dateRange[1].format('YYYY-MM-DD'),
      groupBy,
    });
  };

  const revenueData = report.map((item) => ({
    date: item.period,
    value: item.revenue,
  }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div>
      {overviewError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load billing overview"
          description={overviewError.message}
          action={
            <Button size="small" onClick={refetch}>
              Retry
            </Button>
          }
        />
      )}

      {reportError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Revenue trend is temporarily unavailable"
          description={reportError.message}
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      )}

      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Billing Dashboard</Title>
        </Col>
        <Col>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates) {
                  setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
                }
              }}
            />
            <Select value={groupBy} onChange={setGroupBy} style={{ width: 100 }}>
              <Option value="day">Day</Option>
              <Option value="week">Week</Option>
              <Option value="month">Month</Option>
            </Select>
            <Button type="primary" onClick={handleRefresh}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <MetricCard
            title="This Month"
            value={overview?.mrr || 0}
            prefix="₫"
            precision={0}
            icon={<DollarOutlined />}
            color="#52c41a"
            loading={loading}
          />
        </Col>
        <Col span={6}>
          <MetricCard
            title="Last Month"
            value={overview?.arr ? Math.round(overview.arr / 12) : 0}
            prefix="₫"
            precision={0}
            icon={<DollarOutlined />}
            color="#1890ff"
            loading={loading}
          />
        </Col>
        <Col span={6}>
          <MetricCard
            title="Active Subscriptions"
            value={overview?.activeSubscriptions || 0}
            icon={<ShoppingOutlined />}
            color="#722ed1"
            loading={loading}
            trend={8.2}
            trendLabel="vs last month"
          />
        </Col>
        <Col span={6}>
          <MetricCard
            title="Past Due"
            value={overview?.pastDueSubscriptions || 0}
            icon={<WarningOutlined />}
            color="#f5222d"
            loading={loading}
            trend={-2.1}
            trendLabel="vs last month"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <BarChart
            title="Revenue (6 months)"
            data={revenueData.length > 0 ? revenueData : []}
            loading={reportLoading}
            yAxis={{
              title: 'Revenue (₫)',
              formatter: (v: number) => `₫${(v / 1000000).toFixed(0)}M`,
            }}
            tooltip={{
              formatter: (datum: any) => ({
                name: 'Revenue',
                value: formatCurrency(datum.value),
              }),
            }}
            color="#1890ff"
          />
        </Col>
        <Col span={12}>
          <Card title="Revenue by Plan">
            <Space direction="vertical" style={{ width: '100%' }}>
              {!loading && (!overview?.revenueByPlan || overview.revenueByPlan.length === 0) ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No plan revenue data" />
              ) : (
                overview?.revenueByPlan?.map((item) => (
                  <div key={item.plan}>
                    <Row justify="space-between">
                      <Col>
                        <Tag color="blue">{item.plan.toUpperCase()}</Tag>
                      </Col>
                      <Col>{formatCurrency(item.revenue)}</Col>
                    </Row>
                    <Progress
                      percent={item.percentage}
                      size="small"
                      strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                      }}
                      style={{ marginTop: 4 }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.count} subscriptions
                    </Text>
                  </div>
                ))
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="Recent Invoices">
        <Table
          columns={[
            { title: 'Invoice #', dataIndex: 'id', key: 'id' },
            {
              title: 'Tenant',
              dataIndex: 'tenantName',
              key: 'tenantName',
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              key: 'amount',
              render: (v) => formatCurrency(v),
              align: 'right',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              render: (status) => (
                <Tag
                  color={
                    status === 'paid'
                      ? 'success'
                      : status === 'pending'
                      ? 'warning'
                      : status === 'failed'
                      ? 'error'
                      : 'default'
                  }
                >
                  {status.toUpperCase()}
                </Tag>
              ),
            },
            {
              title: 'Issued',
              dataIndex: 'issuedAt',
              key: 'issuedAt',
              render: (v) => dayjs(v).format('YYYY-MM-DD'),
            },
            {
              title: 'Due',
              dataIndex: 'dueDate',
              key: 'dueDate',
              render: (v) => dayjs(v).format('YYYY-MM-DD'),
            },
          ]}
          dataSource={overview?.recentInvoices || []}
          loading={loading}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent invoices" /> }}
          pagination={{ pageSize: 5 }}
          rowKey="id"
        />
      </Card>
    </div>
  );
};

export default BillingDashboard;
